import { amrIsAllowed } from "@/lib/auth/admin-auth-methods.shared";
import { isNowadaysAdminUserId } from "@/lib/db/admins";
import { getCurrentUser } from "@/lib/db/users";
import { Users } from "@/lib/users/types";
import { SupabaseClient } from "@supabase/supabase-js";
import { create } from "zustand";
import { isUserEmailNorthstarAdmin } from "../db/northstar_admins";
import { isUserEmailWhitelisted } from "../db/whitelisted_users";

/**
 * How many failed `fetchUser` attempts to allow before latching. A failure retries
 * at once (the `useUser` effect re-fires when `isLoading` drops), so an error that
 * never clears would otherwise loop forever. A login linked to nobody is such an
 * error: `getCurrentUser` throws for it on every attempt (ENG-726).
 */
const MAX_FETCH_ATTEMPTS = 3;

/**
 * Client-side mirror of the server admin gate: is the user in `nowadays.admins`
 * AND signed in via an allowed MFA path (Google OAuth / SAML SSO)?
 *
 * Cosmetic ONLY: it keeps admin UI affordances from showing for a session that
 * would be denied server-side (which would then 403). The authoritative gates are
 * the server checks (`requireAdmin`, `requireNowadaysAdmin`, `getCachedAdminAccess`).
 * Fails closed to `false` on any `getClaims` error.
 *
 * Keyed on the person `getCurrentUser` resolved through the login's link, never the
 * session's email claim (ENG-726).
 *
 * @param supabase - The browser Supabase client.
 * @param userId - The current person's `nowadays.users.user_id`.
 * @returns Whether to surface admin UI for this session.
 */
async function resolveClientAdminStatus(supabase: SupabaseClient, userId: number): Promise<boolean> {
  if (!(await isNowadaysAdminUserId(supabase, userId))) return false;
  try {
    const { data } = await supabase.auth.getClaims();
    return amrIsAllowed(data?.claims?.amr);
  } catch {
    return false;
  }
}

interface UserStore {
  user: Users | null;
  isNowadaysAdmin: boolean;
  isNorthstarAdmin: boolean;
  isWhitelisted: boolean;
  isLoading: boolean;
  /**
   * True once a `fetchUser` attempt has completed cleanly (a resolved user OR an anonymous
   * `null`), or after {@link MAX_FETCH_ATTEMPTS} thrown errors in a row. A single thrown error
   * stays unlatched so a transient failure can retry. Latches the one-shot
   * fetch so consumers like `useUser` don't re-trigger it: for a logged-out visitor `user` stays
   * null forever, so without this flag the effect's `!user && !isLoading` guard re-fires on every
   * `isLoading` toggle, spinning fetchUser in an infinite loop. Reset by `clearUser` so a fresh
   * attempt runs after logout.
   */
  hasFetched: boolean;
  /** Thrown `fetchUser` attempts since the last clean fetch or `clearUser`. */
  failedFetches: number;
  fetchUser: (supabase: SupabaseClient) => Promise<void>;
  refetchUser: (supabase: SupabaseClient) => Promise<void>;
  clearUser: () => void;
}

/**
 * Browser store for the current person, resolved once per tab through the login's link (never the
 * session's email claim, ENG-726), plus the flags that gate UI: staff, Northstar admin, whitelist.
 * Read it through `useUser`, which triggers the one-shot `fetchUser`.
 *
 * @returns The zustand store hook.
 */
export const useUserStore = create<UserStore>((set, get) => ({
  user: null,
  isNowadaysAdmin: false,
  isNorthstarAdmin: false,
  isWhitelisted: false,
  isLoading: false,
  hasFetched: false,
  failedFetches: 0,

  fetchUser: async (supabase: SupabaseClient) => {
    // Prevent duplicate fetches AND re-fetch loops. `hasFetched` latches the one-shot attempt so a
    // logged-out visitor (whose `user` never becomes truthy) doesn't spin fetchUser forever.
    const state = useUserStore.getState();
    if (state.isLoading || state.user || state.hasFetched) {
      return;
    }

    set({ isLoading: true });
    try {
      const currentUser = await getCurrentUser(supabase);
      set({ user: currentUser });

      // `currentUser` is the linked person, so its stored email is safe for the address checks.
      if (currentUser?.email) {
        const adminStatus = await resolveClientAdminStatus(supabase, currentUser.user_id);
        set({ isNowadaysAdmin: adminStatus });
        const northstarAdminStatus = await isUserEmailNorthstarAdmin(supabase, currentUser.email);
        set({ isNorthstarAdmin: northstarAdminStatus });
        const whitelistedStatus = await isUserEmailWhitelisted(supabase, currentUser.email);
        set({ isWhitelisted: whitelistedStatus });
      } else {
        set({ isNowadaysAdmin: false });
        set({ isNorthstarAdmin: false });
        set({ isWhitelisted: false });
      }

      // Latch ONLY on a clean fetch (including anonymous → null). A thrown error skips this and
      // leaves `hasFetched` false, so the effect can retry instead of wedging a logged-in user
      // into a logged-out view on a transient failure.
      set({ hasFetched: true, failedFetches: 0 });
    } catch (error) {
      console.error("fetchUser:", error);
      const failedFetches = get().failedFetches + 1;
      set({
        user: null,
        isNowadaysAdmin: false,
        isNorthstarAdmin: false,
        isWhitelisted: false,
        failedFetches,
        hasFetched: failedFetches >= MAX_FETCH_ATTEMPTS,
      });
    } finally {
      set({ isLoading: false });
    }
  },

  /** Silent re-fetch that does not set isLoading, avoiding UI flicker in consumers. */
  refetchUser: async (supabase: SupabaseClient) => {
    try {
      const currentUser = await getCurrentUser(supabase);
      if (!currentUser) return;
      set({ user: currentUser });

      if (currentUser.email) {
        const [adminStatus, northstarAdminStatus, whitelistedStatus] = await Promise.all([
          resolveClientAdminStatus(supabase, currentUser.user_id),
          isUserEmailNorthstarAdmin(supabase, currentUser.email),
          isUserEmailWhitelisted(supabase, currentUser.email),
        ]);
        set({ isNowadaysAdmin: adminStatus, isNorthstarAdmin: northstarAdminStatus, isWhitelisted: whitelistedStatus });
      }
    } catch (error) {
      console.error("Error refetching user:", error);
    }
  },

  clearUser: () =>
    set({
      user: null,
      isNowadaysAdmin: false,
      isNorthstarAdmin: false,
      isWhitelisted: false,
      isLoading: false,
      hasFetched: false,
      failedFetches: 0,
    }),
}));
